import { useEffect, useState } from 'react'
import { FolderOpen, FolderSync } from 'lucide-react'
import { Button } from '@/components/ui'
import { trpc } from '@/lib/trpc'
import { useAppStore } from '@/stores/app.store'

// Die File System Access API (showDirectoryPicker, Permission-Methoden auf den Handles) ist in
// TypeScripts lib.dom.d.ts nur unvollständig typisiert — deshalb an den API-Rändern bewusst `any`.
type DirHandle = FileSystemDirectoryHandle & {
  queryPermission?: (opts: { mode: 'readwrite' }) => Promise<'granted' | 'denied' | 'prompt'>
  requestPermission?: (opts: { mode: 'readwrite' }) => Promise<'granted' | 'denied' | 'prompt'>
}

const KIND_FOLDER: Record<string, string> = {
  quote_pdf: 'Angebote',
  invoice_pdf: 'Rechnungen',
  contract: 'Vertraege',
  receipt: 'Belege',
}

const DB_NAME = 'mavatech-local-sync'
const STORE = 'handles'
const KEY = 'exportDir'

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => req.result.createObjectStore(STORE)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function saveHandle(handle: DirHandle): Promise<void> {
  const db = await openDb()
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).put(handle, KEY)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

async function loadHandle(): Promise<DirHandle | null> {
  try {
    const db = await openDb()
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly')
      const req = tx.objectStore(STORE).get(KEY)
      req.onsuccess = () => resolve((req.result as DirHandle) ?? null)
      req.onerror = () => reject(req.error)
    })
  } catch {
    return null
  }
}

/**
 * Lokaler Ordner-Sync: einmal einen Ordner verbinden (File System Access API), danach lädt
 * "Jetzt synchronisieren" nur noch Dateien herunter, die dort noch nicht liegen — Chrome/Edge
 * merken sich die Berechtigung, kein erneutes Auswählen nötig. Safari/Firefox unterstützen die
 * API nicht; dort bleibt der ZIP-Download (oben) der Weg.
 */
export function LocalSyncPanel() {
  const addToast = useAppStore((s) => s.addToast)
  const supported = typeof window !== 'undefined' && 'showDirectoryPicker' in window
  const [handle, setHandle] = useState<DirHandle | null>(null)
  const [dirName, setDirName] = useState<string | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [lastResult, setLastResult] = useState<string | null>(null)
  const list = trpc.asset.listExportable.useQuery(undefined, { enabled: supported })

  useEffect(() => {
    if (!supported) return
    loadHandle().then((h) => {
      if (h) {
        setHandle(h)
        setDirName(h.name)
      }
    })
  }, [supported])

  const pickFolder = async () => {
    try {
      const h = (await (window as unknown as { showDirectoryPicker: (o: unknown) => Promise<DirHandle> }).showDirectoryPicker({
        mode: 'readwrite',
      })) as DirHandle
      await saveHandle(h)
      setHandle(h)
      setDirName(h.name)
      setLastResult(null)
      addToast({ type: 'success', title: `Ordner "${h.name}" verbunden` })
    } catch (e) {
      if ((e as { name?: string })?.name !== 'AbortError') {
        addToast({ type: 'error', title: 'Fehler', description: String((e as Error)?.message ?? e) })
      }
    }
  }

  const sync = async () => {
    if (!handle || !list.data) return
    setSyncing(true)
    let created = 0
    let skipped = 0
    let failed = 0
    try {
      if (handle.queryPermission) {
        let perm = await handle.queryPermission({ mode: 'readwrite' })
        if (perm !== 'granted' && handle.requestPermission) perm = await handle.requestPermission({ mode: 'readwrite' })
        if (perm !== 'granted') {
          addToast({ type: 'error', title: 'Zugriff auf den Ordner wurde nicht erteilt' })
          setSyncing(false)
          return
        }
      }

      for (const a of list.data) {
        const folderName = KIND_FOLDER[a.kind] ?? 'Sonstiges'
        const subDir = await handle.getDirectoryHandle(folderName, { create: true })
        const filename = a.filename || `${a.id}.pdf`

        let exists = true
        try {
          await subDir.getFileHandle(filename)
        } catch {
          exists = false
        }
        if (exists) {
          skipped++
          continue
        }

        try {
          const res = await fetch(`/export/asset/${a.id}`, { credentials: 'include' })
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          const blob = await res.blob()
          const fileHandle = await subDir.getFileHandle(filename, { create: true })
          const writable = await fileHandle.createWritable()
          await writable.write(blob)
          await writable.close()
          created++
        } catch {
          failed++
        }
      }
      const summary = `${created} neu gespeichert, ${skipped} bereits vorhanden${failed ? `, ${failed} fehlgeschlagen` : ''}`
      setLastResult(summary)
      addToast({ type: created || failed ? 'success' : 'success', title: 'Synchronisierung fertig', description: summary })
    } finally {
      setSyncing(false)
    }
  }

  if (!supported) {
    return (
      <p className="text-xs text-text-tertiary">
        Der automatische Ordner-Sync funktioniert nur in Chrome oder Edge (Browser-Funktion "File
        System Access"). Nutze in anderen Browsern den ZIP-Download oben.
      </p>
    )
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-text-secondary">
        {dirName ? (
          <>
            Verbundener Ordner: <span className="font-medium text-text-primary">{dirName}</span>
          </>
        ) : (
          'Noch kein Ordner verbunden.'
        )}
      </p>
      <div className="flex items-center gap-2">
        <Button variant="secondary" onClick={pickFolder}>
          <FolderOpen className="w-4 h-4" /> {dirName ? 'Anderen Ordner wählen' : 'Ordner auswählen'}
        </Button>
        {handle && (
          <Button variant="primary" onClick={sync} loading={syncing} disabled={!list.data}>
            <FolderSync className="w-4 h-4" /> Jetzt synchronisieren
          </Button>
        )}
      </div>
      {lastResult && <p className="text-2xs text-text-tertiary">{lastResult}</p>}
      <p className="text-2xs text-text-tertiary">
        Beim ersten Mal lädt das alle Dokumente herunter, danach nur noch neu hinzugekommene —
        vorhandene Dateien im Ordner werden nicht angerührt.
      </p>
    </div>
  )
}
